/**
 * @author Nihar <nihar.baishyar@314ecorp.com>
 * @description utils for metadata
 */

import _ from 'lodash';
import { isEkycCompletedNpi } from './admin/npi';
import { ELIGIBILITY_PAYER_STATUS_CODES, ELIGIBILITY_PAYER_TAG_STATUS } from 'app-constants/eligibility/index';
import { PROVIDER_STATUS } from 'app-constants/admin/provider';

const EMPTY_LOCATION_VALUE = 'empty-location';

// whole npi meta data
const getProviderMetaData = (providerList: Provider.IPaginatedResponseModel[]): Metadata.IProviderMetaObj => {
	return _.keyBy(
		_.map(providerList, (item) => {
			const ekycStatus = item?.ekycDetails?.verificationStatus;
			const isEkycCompleted = isEkycCompletedNpi(ekycStatus);
			const isActive = item?.activeStatus === PROVIDER_STATUS.active.value;
			return {
				id: item?.id,
				ekycStatus,
				npi: item?.npi,
				isActive,
				isEkycCompleted,
				canMakeRequests: isActive && isEkycCompleted,
				name: item?.name,
				providerType: item?.providerType,
				entity: item,
			};
		}),
		(meta) => meta?.id,
	);
};

// active and ekyc completed providers
const getActiveAndEkycCompletedProviders = (providerMeta: Metadata.IProviderMetaObj): Metadata.IProviderMetaObj => {
	return _.pickBy(providerMeta, ({ isEkycCompleted, isActive }) => isEkycCompleted && isActive);
};

// get providers which are active but ekyc is not done
const getActiveAndEkycInCompletedProviders = (providerMeta: Metadata.IProviderMetaObj): Metadata.IProviderMetaObj => {
	return _.pickBy(providerMeta, ({ isEkycCompleted, isActive }) => !isEkycCompleted && isActive);
};

// get all inactive providers
const getInActiveProviders = (providerMeta: Metadata.IProviderMetaObj): Metadata.IProviderMetaObj => {
	return _.pickBy(providerMeta, ({ isActive }) => !isActive);
};

// provider group options
const prepareProviderGroupOptions = (providerMeta: Metadata.IProviderMetaObj): Metadata.INpiMetaDataOptions[] => {
	const activeAndEkycCompletedProviders = getActiveAndEkycCompletedProviders(providerMeta);
	const activeAndEkycInCompletedProviders = getActiveAndEkycInCompletedProviders(providerMeta);
	const inActiveProviders = getInActiveProviders(providerMeta);
	const providerGroupOptions = [];
	if (!_.isEmpty(activeAndEkycCompletedProviders)) {
		_.map(_.values(activeAndEkycCompletedProviders), ({ name, id }) => providerGroupOptions.push({ label: name, value: id }));
	}
	if (!_.isEmpty(activeAndEkycInCompletedProviders)) {
		providerGroupOptions.push({
			label: 'Identity Verification Required',
			options: _.map(_.values(activeAndEkycInCompletedProviders), ({ name, id, isEkycCompleted, isActive, entity }) => ({
				label: name,
				value: id,
				disabled: true,
				isEkycCompleted,
				isActive,
				entity,
			})),
		});
	}
	if (!_.isEmpty(inActiveProviders)) {
		providerGroupOptions.push({
			label: 'Inactive Providers',
			options: _.map(_.values(inActiveProviders), ({ name, id, isEkycCompleted, isActive }) => ({
				label: name,
				value: id,
				disabled: true,
				isEkycCompleted,
				isActive,
			})),
		});
	}
	return providerGroupOptions;
};

// prepare provider group options for previous requests screen
const preparePreviousRequestProviderGroupOptions = (providerMeta: Metadata.IProviderMetaObj): Metadata.INpiMetaDataOptions[] => {
	const activeAndEkycCompletedProviders = getActiveAndEkycCompletedProviders(providerMeta);
	const activeAndEkycInCompletedProviders = getActiveAndEkycInCompletedProviders(providerMeta);
	const inActiveProviders = getInActiveProviders(providerMeta);
	const allInactiveProviders = _.concat(_.values(activeAndEkycInCompletedProviders), _.values(inActiveProviders));
	const previousRequestProviderGroupOptions = [];
	if (!_.isEmpty(activeAndEkycCompletedProviders)) {
		_.map(_.values(activeAndEkycCompletedProviders), ({ name, id }) =>
			previousRequestProviderGroupOptions.push({ label: name, value: id }),
		);
	}
	if (!_.isEmpty(allInactiveProviders)) {
		previousRequestProviderGroupOptions.push({
			label: 'Inactive',
			options: _.map(_.values(allInactiveProviders), ({ name, id }) => ({
				label: name,
				value: id,
			})),
		});
	}
	return previousRequestProviderGroupOptions;
};

// location meta
const getLocationMetaData = (locationList: Location.IModel[]): Record<string, Metadata.ILocationMeta> => {
	return _.keyBy(
		_.map(locationList, (item) => {
			const isActive = item?.status;
			return {
				isActive,
				name: item?.name || '',
				serviceLocationId: item?.id,
			};
		}),
		(meta) => meta?.serviceLocationId,
	);
};

// active locations
const getActiveLocations = (locationMeta: Record<string, Metadata.ILocationMeta>): Record<string, Metadata.ILocationMeta> => {
	return _.pickBy(locationMeta, ({ isActive }) => isActive);
};

// inactive locations
const getInActiveLocations = (locationMeta: Record<string, Metadata.ILocationMeta>): Record<string, Metadata.ILocationMeta> => {
	return _.pickBy(locationMeta, ({ isActive }) => !isActive);
};

// location group options
const prepareLocationGroupOptions = (
	locationMeta: Record<string, Metadata.ILocationMeta>,
	shouldEnableInactiveOptions?: boolean,
): Metadata.ILocationMetaDataOptions[] => {
	const activeLocations = getActiveLocations(locationMeta);
	const inActiveLocations = getInActiveLocations(locationMeta);
	const locationGroupOptions = [];
	if (!_.isEmpty(activeLocations)) {
		_.map(_.values(activeLocations), ({ name, serviceLocationId }) =>
			locationGroupOptions.push({ label: name, value: serviceLocationId }),
		);
	}
	if (!_.isEmpty(inActiveLocations)) {
		locationGroupOptions.push({
			label: 'Inactive Locations',
			options: _.map(_.values(inActiveLocations), ({ name, serviceLocationId }) => ({
				label: name,
				value: serviceLocationId,
				...(!shouldEnableInactiveOptions && { disabled: true }),
			})),
		});
	}
	return locationGroupOptions;
};

// all eligibility payers
const getEligibilityPayers = (allPayers: Payers.IRegisteredPayer[]): Record<string, Payers.IRegisteredPayer> => {
	const eligibilityPayers = _.filter(allPayers, ({ isEligibility }) => isEligibility);
	return _.keyBy(eligibilityPayers, ({ id }) => id);
};

// all claim status payers
const getClaimStatusPayers = (allPayers: Payers.IRegisteredPayer[]): Record<string, Payers.IRegisteredPayer> => {
	const claimStatusPayers = _.filter(allPayers, ({ isClaimStatus }) => isClaimStatus);
	return _.keyBy(claimStatusPayers, ({ id }) => id);
};

// selected active eligibility payers
const getSelectedActiveEligibilityPayers = (selectedPayers: Payers.ISelectedPayer[]): Record<string, Payers.ISelectedPayer> => {
	const selectedActiveEligibilityPayers = _.filter(
		selectedPayers,
		({ isEligibility, status }) => isEligibility && status === ELIGIBILITY_PAYER_STATUS_CODES.active,
	);
	return _.keyBy(selectedActiveEligibilityPayers, ({ id }) => id);
};

// selected but pending status payers
const getSelectedPendingEligibilityPayers = (selectedPayers: Payers.ISelectedPayer[]): Record<string, Payers.ISelectedPayer> => {
	const selectedActiveEligibilityPayers = _.filter(
		selectedPayers,
		({ isEligibility, status }) => isEligibility && status === ELIGIBILITY_PAYER_STATUS_CODES.pending,
	);
	return _.keyBy(selectedActiveEligibilityPayers, ({ id }) => id);
};

// selected inactive payers
const getSelectedInActiveEligibilityPayers = (selectedPayers: Payers.ISelectedPayer[]): Record<string, Payers.ISelectedPayer> => {
	const selectedInActiveEligibilityPayers = _.filter(
		selectedPayers,
		({ isEligibility, status }) => isEligibility && status === ELIGIBILITY_PAYER_STATUS_CODES.inactive,
	);
	return _.keyBy(selectedInActiveEligibilityPayers, ({ id }) => id);
};

// all selected eligibility payers
const getSelectedEligibilityPayers = (selectedPayers: Payers.ISelectedPayer[]): Record<string, Payers.ISelectedPayer> => {
	const selectedEligibilityPayers = _.filter(selectedPayers, ({ isEligibility }) => isEligibility);
	return _.keyBy(selectedEligibilityPayers, ({ id }) => id);
};

// selected active claim status payers
const getSelectedActiveClaimStatusPayers = (selectedPayers: Payers.ISelectedPayer[]): Record<string, Payers.ISelectedPayer> => {
	const selectedActiveClaimStatusPayers = _.filter(
		selectedPayers,
		({ isClaimStatus, status }) => isClaimStatus && status === ELIGIBILITY_PAYER_STATUS_CODES.active,
	);
	return _.keyBy(selectedActiveClaimStatusPayers, ({ id }) => id);
};

// payer group options
const prepareEligibilityPayerGroupOptions = (
	allPayers: Payers.IRegisteredPayer[],
	selectedPayers: Payers.ISelectedPayer[],
): Metadata.IEligibilityPayerMetaDataOptions[] => {
	const payerGroupOptions = [];
	const eligibilityPayers = getEligibilityPayers(allPayers);
	const selectedActiveEligibilityPayers = getSelectedActiveEligibilityPayers(selectedPayers);
	const selectedPendingEligibilityPayers = getSelectedPendingEligibilityPayers(selectedPayers);
	const selectedInActiveEligibilityPayers = getSelectedInActiveEligibilityPayers(selectedPayers);
	const filteredEligibilityPayers = _.omit(eligibilityPayers, [
		..._.keys(selectedActiveEligibilityPayers),
		..._.keys(selectedPendingEligibilityPayers),
		..._.keys(selectedInActiveEligibilityPayers),
	]);

	const getSortedOptions = (options: Record<string, Payers.ISelectedPayer>) => {
		return _.sortBy(_.values(options), 'name');
	};

	if (!_.isEmpty(selectedActiveEligibilityPayers)) {
		_.map(getSortedOptions(selectedActiveEligibilityPayers), ({ name, id, enrollmentRequired }) =>
			payerGroupOptions.push({
				label: name,
				value: id,
				isRequiredEnrollment: enrollmentRequired,
				isSelected: true,
			}),
		);
	}
	if (!_.isEmpty(selectedPendingEligibilityPayers)) {
		_.map(getSortedOptions(selectedPendingEligibilityPayers), ({ name, id, enrollmentRequired }) =>
			payerGroupOptions.push({
				label: name,
				value: id,
				isRequiredEnrollment: enrollmentRequired,
				isSelected: true,
			}),
		);
	}
	if (!_.isEmpty(filteredEligibilityPayers)) {
		_.map(_.sortBy(_.values(filteredEligibilityPayers), 'name'), ({ name, id, enrollmentRequired }) =>
			payerGroupOptions.push({ label: name, value: id, isRequiredEnrollment: enrollmentRequired }),
		);
	}
	if (!_.isEmpty(selectedInActiveEligibilityPayers)) {
		payerGroupOptions.push({
			label: 'Inactive Payers',
			options: _.map(getSortedOptions(selectedInActiveEligibilityPayers), ({ name, id, enrollmentRequired }) => ({
				label: name,
				value: id,
				disabled: true,
				isRequiredEnrollment: enrollmentRequired,
			})),
		});
	}
	return payerGroupOptions;
};

/**
 * group payers by status for multiselect
 */
const getPayerOptionsByStatus = (payers: Payers.ISelectedPayer[], status: Payers.IPayerStatus): IMultiSelectOption[] => {
	const filteredPayers = _.filter(payers, (payer) => payer.status === status);
	return _.map(filteredPayers, ({ name, id }) => ({ label: name, value: id }));
};

// eligibility selected payers options for eligibility  form
const getEligibilitySelectedPayersOptions = (selectedPayers: Payers.ISelectedPayer[]): (IMultiSelectOption | IMultiSelectGroupOption)[] => {
	const eligibilityPayers = _.filter(selectedPayers, (payer) => payer.isEligibility);
	const activeEligibilityPayerOptions = getPayerOptionsByStatus(eligibilityPayers, ELIGIBILITY_PAYER_STATUS_CODES.active);
	const inactiveEligibilityPayerOptions = getPayerOptionsByStatus(eligibilityPayers, ELIGIBILITY_PAYER_STATUS_CODES.inactive);
	const archivedEligibilityPayerOptions = getPayerOptionsByStatus(eligibilityPayers, ELIGIBILITY_PAYER_STATUS_CODES.archived);
	const eligibilitySelectedPayersOptions: (IMultiSelectOption | IMultiSelectGroupOption)[] = activeEligibilityPayerOptions;
	if (!_.isEmpty(inactiveEligibilityPayerOptions)) {
		eligibilitySelectedPayersOptions.push({
			label: ELIGIBILITY_PAYER_TAG_STATUS.inactive.label,
			options: inactiveEligibilityPayerOptions,
		});
	}
	if (!_.isEmpty(archivedEligibilityPayerOptions)) {
		eligibilitySelectedPayersOptions.push({
			label: ELIGIBILITY_PAYER_TAG_STATUS.archived.label,
			options: archivedEligibilityPayerOptions,
		});
	}
	return eligibilitySelectedPayersOptions;
};

// claim status selected payers options for claim status form
const getClaimStatusSelectedPayersOptions = (selectedPayers: Payers.ISelectedPayer[]): (IMultiSelectOption | IMultiSelectGroupOption)[] => {
	const claimStatusPayers = _.filter(selectedPayers, (payer) => payer.isClaimStatus);
	const activeClaimStatusPayerOptions = getPayerOptionsByStatus(claimStatusPayers, ELIGIBILITY_PAYER_STATUS_CODES.active);
	const inactiveClaimStatusPayerOptions = getPayerOptionsByStatus(claimStatusPayers, ELIGIBILITY_PAYER_STATUS_CODES.inactive);
	const archivedClaimStatusPayerOptions = getPayerOptionsByStatus(claimStatusPayers, ELIGIBILITY_PAYER_STATUS_CODES.archived);
	const claimStatusSelectedPayersOptions: (IMultiSelectOption | IMultiSelectGroupOption)[] = activeClaimStatusPayerOptions;
	if (!_.isEmpty(inactiveClaimStatusPayerOptions)) {
		claimStatusSelectedPayersOptions.push({
			label: ELIGIBILITY_PAYER_TAG_STATUS.inactive.label,
			options: inactiveClaimStatusPayerOptions,
		});
	}
	if (!_.isEmpty(archivedClaimStatusPayerOptions)) {
		claimStatusSelectedPayersOptions.push({
			label: ELIGIBILITY_PAYER_TAG_STATUS.archived.label,
			options: archivedClaimStatusPayerOptions,
		});
	}
	return claimStatusSelectedPayersOptions;
};

export {
	EMPTY_LOCATION_VALUE,
	getActiveLocations,
	getProviderMetaData,
	getActiveAndEkycCompletedProviders,
	getLocationMetaData,
	prepareProviderGroupOptions,
	prepareLocationGroupOptions,
	prepareEligibilityPayerGroupOptions,
	getEligibilityPayers,
	getClaimStatusPayers,
	getSelectedEligibilityPayers,
	getSelectedActiveClaimStatusPayers,
	getSelectedActiveEligibilityPayers,
	getEligibilitySelectedPayersOptions,
	getClaimStatusSelectedPayersOptions,
	preparePreviousRequestProviderGroupOptions,
};
